import { useEffect, useRef, useState } from 'react'
import { VisuallyHidden } from '@n26/nxd'
import {
  TIMEFRAME_FILTER,
  type DashboardSection,
} from '@/features/dashboard/api/schemas'
import { useDashboardStructure } from '@/features/dashboard/api/queries'
import { sectionTakes } from '@/features/dashboard/model/sections'
import { MarketChips } from './MarketChips'
import { SelectFilter } from './SelectFilter'
import { useDashboardFilters, useSectionSelection } from './useDashboardFilters'
import { useSectionMarkets } from './useSectionMarkets'
import styles from './SectionFilters.module.css'

/**
 * Says what a filter now selects when the selection changes (not when the
 * filter first appears), for screen readers.
 */
const useAnnouncement = (summary: string, prefix: string) => {
  const [announcement, setAnnouncement] = useState('')
  const previous = useRef<string | null>(null)
  useEffect(() => {
    if (previous.current !== null && previous.current !== summary) {
      setAnnouncement(`${prefix}: ${summary}`)
    }
    previous.current = summary
  }, [summary, prefix])
  return announcement
}

/**
 * A section's timeframe and range, for the expanded card's chart header (the
 * design's "Timeframe: [YTD] [Current Year]"). Renders nothing for a section
 * none of whose cards takes the timeframe.
 */
export const TimeframeFilters = ({
  section,
}: {
  section: DashboardSection
}) => {
  const { data: dashboard } = useDashboardStructure()
  const { selection, setTimeframe, setPeriods } = useSectionSelection(
    section.id
  )
  const timeframeOptions = dashboard?.filters?.timeframes
  const current = timeframeOptions?.find(
    option => option.id === selection?.timeframe
  )
  const show = sectionTakes(section, TIMEFRAME_FILTER) && selection && current

  const rangeLabel = current?.periods.options.find(
    option => option.value === selection?.periods
  )?.label
  const announcement = useAnnouncement(
    show ? `${selection.timeframe}, ${rangeLabel ?? ''}` : '',
    section.title
  )

  if (!show) return null

  return (
    <div className={styles.timeframe}>
      <VisuallyHidden role='status'>{announcement}</VisuallyHidden>
      <span className={styles.label} aria-hidden='true'>
        Timeframe:
      </span>
      <SelectFilter
        label={`${section.title} timeframe`}
        hideLabel
        options={(timeframeOptions ?? []).map(option => ({
          value: option.id,
          label: option.id,
        }))}
        value={selection.timeframe}
        onChange={setTimeframe}
      />
      <SelectFilter
        label={`${section.title} range`}
        hideLabel
        plain
        // A lone range (YTD, Annual) is named, not chosen.
        readOnly={current.periods.options.length === 1}
        options={current.periods.options}
        value={selection.periods}
        onChange={setPeriods}
      />
    </div>
  )
}

/**
 * A section's market chips, for its header row (Customers only): the markets
 * of the cards that take the market. Renders nothing when none does.
 */
export const MarketFilters = ({ section }: { section: DashboardSection }) => {
  const { selectedMarkets, isMarketFiltered } = useDashboardFilters()
  const markets = useSectionMarkets(section)

  const summary = !markets
    ? ''
    : !isMarketFiltered
      ? 'all markets'
      : markets
          .filter(market => selectedMarkets.includes(market.id))
          .map(market => market.label)
          .join(', ')
  const announcement = useAnnouncement(summary, section.title)

  if (!markets) return null

  return (
    <>
      <VisuallyHidden role='status'>{announcement}</VisuallyHidden>
      <MarketChips markets={markets} />
    </>
  )
}
